import { useState, useEffect } from 'react';
import { useAppData } from '../context/DataContext';

/**
 * A unified hook to manage user prompts with prioritization:
 * 1. Quiz completion
 * 2. Phone number collection
 * 
 * Includes session management to prevent showing prompts too frequently
 */
const useUserPrompts = () => {
  const { userInfo } = useAppData();
  const [activePrompt, setActivePrompt] = useState(null); // 'quiz', 'phone', or null

  useEffect(() => {
    // Only run this if we have user info (user is logged in)
    if (userInfo && userInfo._id) {
      // Check if we've already shown a prompt this session
      const promptShownThisSession = sessionStorage.getItem('prompt_shown_this_session') === 'true';
      
      // If we've shown a prompt in this session, don't show another
      if (promptShownThisSession) {
        return;
      }

      // Check for quiz completion first
      if (!userInfo.quizResponse) {
        // Check if this is a new session by looking for a session timestamp in localStorage
        const lastSessionTime = localStorage.getItem('ummahfy_session_timestamp');
        const currentTime = new Date().getTime();
        
        // Consider it a new session if:
        // 1. No previous session exists, or
        // 2. It's been more than 6 hours since the last session
        const isNewSession = !lastSessionTime || 
          (currentTime - parseInt(lastSessionTime, 10)) > (6 * 60 * 60 * 1000);
        
        // Update the session timestamp
        localStorage.setItem('ummahfy_session_timestamp', currentTime.toString());
        
        // Only show prompt if it's a new session
        if (isNewSession) {
          // Small delay to ensure the app has fully loaded
          setTimeout(() => {
            setActivePrompt('quiz');
            sessionStorage.setItem('prompt_shown_this_session', 'true');
          }, 1000);
        }
      } 
      // Only check for phone if quiz is completed
      else if (!userInfo.phoneNumber) {
        // Check new session same as above
        const lastSessionTime = localStorage.getItem('ummahfy_session_timestamp');
        const currentTime = new Date().getTime();
        const isNewSession = !lastSessionTime || 
          (currentTime - parseInt(lastSessionTime, 10)) > (6 * 60 * 60 * 1000);
        
        // Update the session timestamp
        localStorage.setItem('ummahfy_session_timestamp', currentTime.toString());
        
        // Only show prompt if it's a new session
        if (isNewSession) {
          // Small delay to ensure the app has fully loaded
          setTimeout(() => {
            setActivePrompt('phone');
            sessionStorage.setItem('prompt_shown_this_session', 'true');
          }, 1000);
        }
      }
    }
  }, [userInfo]);

  // Function to clear the prompt and track that a prompt was shown
  const clearPrompt = () => {
    setActivePrompt(null);
  };

  return { activePrompt, clearPrompt };
};

export default useUserPrompts; 